/**
 * The second model class (frontier workstream F-2, registered in
 * validation/simulation/results/FRONTIER_F2_RESULTS.md): the
 * area-level Fay-Herriot model with Student-t random effects.
 *
 *   y_d = x_d' beta + u_d + e_d,   e_d ~ N(0, psi_d),
 *   u_d | w_d ~ N(0, sigma^2 / w_d),   w_d ~ Gamma(nu/2, nu/2),
 *
 * so u_d is marginally Student-t with nu degrees of freedom and scale
 * sigma. The class exists for the limitation the programme record
 * names (Addenda 44, 54, 58): on the real-data arm the shipped normal
 * class pulls genuine regression-outlier areas toward the covariate
 * surface, because a normal random effect charges a large residual
 * quadratically. Under the t, a district whose residual is large
 * relative to the surface is discounted instead — its posterior
 * weight w_d concentrates below 1 — and stands away from the surface
 * at a bounded price. As nu -> infinity the class reduces to the
 * shipped normal model.
 *
 * Fitting (all deterministic; no sampling anywhere):
 *   - nu is estimated per cell fit by profile marginal likelihood
 *     over {3, 4, 5, 7, 10, 15, 25, infinity}; ties within 1e-9 of
 *     log-likelihood break to the larger nu. The infinity endpoint is
 *     the normal marginal-ML fit.
 *   - At fixed nu, (beta, sigma^2) by EM on the scale mixture. The
 *     E-step's posterior moments of (u_d, w_d) given y_d come from
 *     one-dimensional ADAPTIVE Gauss-Legendre quadrature over
 *     t = log w: per area and iteration, the posterior mode in t is
 *     located (coarse scan of [-13.8, 9.2], then damped Newton on
 *     the kernel's analytic derivatives) and 64 nodes are placed on
 *     a window marched outward from the mode to a 30-nat kernel
 *     drop (union the curvature window), clamped to the range, in
 *     two Gauss-Legendre panels split at the mode so the rule's
 *     resolution sits on the posterior spike. A fixed grid cannot
 *     serve here: at large nu the posterior of w concentrates in a
 *     spike narrower than any affordable fixed spacing, which
 *     misstates the profile likelihood exactly where the class
 *     approaches the shipped model (observed in the F-2 build's
 *     smoke tests, and arbitrated against brute-force integration
 *     in the study file's M-1). The M-step is closed form
 *     (psi-weighted least squares on y_d - E[u_d | y_d] for beta; the
 *     mean of E[w_d u_d^2 | y_d] for sigma^2). EM runs at a loose
 *     tolerance for the profile scan, warm-started across the grid;
 *     every candidate within 1e-5 of the best loose log-likelihood
 *     is polished at a tight tolerance before the winner is chosen.
 *     The F-2 measurement phase audits the quadrature (64 vs 192
 *     adaptive nodes, plus an independent brute-force trapezoid
 *     integration) and the maximization (against a reference scan
 *     at tolerance 1e-11) — study file, M-1.
 *   - FayHerriotFit.sigma2v carries the SCALE sigma^2 (the marginal
 *     variance of u_d is sigma^2 * nu / (nu - 2)); robustNu carries
 *     the fitted nu. The parametric bootstrap reads both to draw
 *     replicate effects from the fitted t.
 *
 * EBLUP and MSE (the class's Quick construction, registered):
 * theta-tilde_d = x_d' beta-hat + E[u_d | y_d]; the effective
 * shrinkage gamma-bar_d is E[u_d | y_d] / r_d (the normal shrinkage
 * at the fitted variance when r_d = 0). mseTheta_d = V_d + g2_d +
 * 2 g3_d with V_d = Var(u_d | y_d) in the g1 slot (so the
 * back-transform's s2 = g1 + g2 stays the EBLUP's posterior-scale
 * variance), g2_d = (1 - gamma-bar_d)^2 x_d' (X'QX)^{-1} x_d with
 * Q = diag(1 / (sigma-hat^2 + psi_d)), and g3_d in the shipped REML
 * form. The uncertainty of nu-hat itself is not represented in the
 * MSE (registered limitation). Out-of-sample areas take
 * mseTheta = sigma-hat^2 nu-hat / (nu-hat - 2) + x' (X'QX)^{-1} x in
 * the shipped out-of-sample slot (finite on the whole grid).
 *
 * Decline rule (registered): the class returns the shipped normal
 * cascade fit unchanged when the mixture machinery fails
 * numerically — no grid nu yields a finite fit, or sigma-hat^2 at
 * the best nu is at or below 1e-12. Input validation mirrors the
 * shipped fit exactly (same refusals), and the selection stage is
 * untouched: arms differ only in the cell fit and its MSE.
 */
import { InsufficientAreasError } from './errors.js';
import { MIN_AREAS_TO_FIT } from './fayherriot.js';
import { cholesky, cholInverse, dot, matVec, zeros, } from './linalg.js';
import { gaussLegendre, logGamma } from './numeric.js';
/** The registered profile grid for nu (plus the infinity endpoint). */
export const T_NU_GRID = [3, 4, 5, 7, 10, 15, 25];
const LOG_W_MIN = -13.8;
const LOG_W_MAX = 9.2;
const SIGMA2_DECLINE_FLOOR = 1e-12;
const DEFAULT_QUADRATURE_NODES = 64;
const PROFILE_TOL_GAIN = 1e-8;
const POLISH_TOL_GAIN = 1e-11;
const PROFILE_CAP = 200;
const POLISH_CAP = 300;
const POLISH_WINDOW = 1e-5;
const TIE_BAND = 1e-9;
function meanOf(values) {
    return values.reduce((acc, v) => acc + v, 0) / values.length;
}
function medianOf(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : 0.5 * (sorted[mid - 1] + sorted[mid]);
}
function sampleVariance(values) {
    if (values.length < 2)
        return 0;
    const mean = meanOf(values);
    return (values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) /
        (values.length - 1));
}
function skewnessKurtosis(values) {
    const n = values.length;
    if (n === 0)
        return { skewness: 0, kurtosis: 0 };
    const mean = meanOf(values);
    let m2 = 0;
    let m3 = 0;
    let m4 = 0;
    for (const v of values) {
        const d = v - mean;
        const d2 = d * d;
        m2 += d2;
        m3 += d2 * d;
        m4 += d2 * d2;
    }
    m2 /= n;
    m3 /= n;
    m4 /= n;
    if (m2 <= 0)
        return { skewness: 0, kurtosis: 0 };
    return {
        skewness: m3 / Math.pow(m2, 1.5),
        kurtosis: m4 / (m2 * m2) - 3,
    };
}
function singularError(context) {
    return new Error(`fitFayHerriot: the ${context} matrix is singular or not positive definite; ` +
        'the covariates are collinear. Remove a collinear covariate and refit ' +
        '(brief 9.7 covariate dropping is not applied automatically: the fit ' +
        'result has no channel to report a dropped covariate).');
}
/** Prasad-Rao moment estimator (the shipped fit's start; brief 9.7). */
function prasadRaoSigma2(y, X, psi) {
    const m = y.length;
    const p = X[0].length;
    const XtX = zeros(p, p);
    const Xty = new Array(p).fill(0);
    for (let i = 0; i < m; i++) {
        for (let a = 0; a < p; a++) {
            Xty[a] += X[i][a] * y[i];
            for (let b = 0; b < p; b++)
                XtX[a][b] += X[i][a] * X[i][b];
        }
    }
    const L = cholesky(XtX);
    if (L === null)
        throw singularError('cross-product (X\'X)');
    const invXtX = cholInverse(L);
    const betaOls = matVec(invXtX, Xty);
    let sumU2 = 0;
    let sumPsiH = 0;
    for (let i = 0; i < m; i++) {
        const u = y[i] - dot(X[i], betaOls);
        const h = dot(X[i], matVec(invXtX, X[i]));
        sumU2 += u * u;
        sumPsiH += psi[i] * (1 - h);
    }
    return Math.max(0, (sumU2 - sumPsiH) / (m - p));
}
// ---------------------------------------------------------------------
// Adaptive quadrature over t = log w.
//
// The integrand is the posterior kernel of t for one area:
//   k(t) = c + a t - a e^t  -  (1/2) log(2 pi V) - r^2 / (2 V),
// with a = nu/2, c = a log a - lgamma(a) (the Gamma(a, a) prior in t,
// Jacobian included), V = sigma^2 e^{-t} + psi, r the area's
// residual. Its derivatives, with w = e^t, S = sigma^2 e^{-t}:
//   k'(t)  = a (1 - w) + (S / (2 V^2)) (V - r^2),
//   k''(t) = -a w + (S / (2 V^3)) ((V - r^2) (S - psi) - S V).
// ---------------------------------------------------------------------
function kernelT(t, r, psi, sigma2, a, priorConst) {
    const V = sigma2 * Math.exp(-t) + psi;
    return (priorConst +
        a * t -
        a * Math.exp(t) -
        0.5 * Math.log(2 * Math.PI * V) -
        (r * r) / (2 * V));
}
function kernelDerivs(t, r, psi, sigma2, a) {
    const w = Math.exp(t);
    const S = sigma2 * Math.exp(-t);
    const V = S + psi;
    const d1 = a * (1 - w) + ((S / (2 * V * V)) * (V - r * r));
    const d2 = -a * w + ((S / (2 * V * V * V)) * ((V - r * r) * (S - psi) - S * V));
    return { d1, d2 };
}
/** Coarse fixed nodes used only to locate each area's posterior mode. */
let coarseNodesCache = null;
function coarseNodes() {
    if (coarseNodesCache === null) {
        coarseNodesCache = gaussLegendre(32, LOG_W_MIN, LOG_W_MAX).nodes;
    }
    return coarseNodesCache;
}
/**
 * Gauss-Legendre rules on a 0.1 lattice of windows, cached: EM
 * iterations revisit near-identical windows hundreds of times, and
 * rebuilding the rule (Legendre root-finding) per area per iteration
 * would dominate the fit's cost. The window actually used is the
 * lattice-rounded one, so the rule always matches its key.
 */
const glRuleCache = new Map();
function glRule(nodeCount, lo, hi) {
    const rLo = Math.round(lo * 10) / 10;
    const rHi = Math.round(hi * 10) / 10;
    const key = `${nodeCount}|${rLo}|${rHi}`;
    let rule = glRuleCache.get(key);
    if (rule === undefined) {
        if (glRuleCache.size > 8192)
            glRuleCache.clear();
        rule = gaussLegendre(nodeCount, rLo, rHi);
        glRuleCache.set(key, rule);
    }
    return rule;
}
/**
 * Locate the kernel's mode in t: Newton from `start` (the previous
 * iteration's mode when supplied, else the coarse grid's argmax),
 * damped to steps of at most 1.5. Returns the mode and the curvature
 * scale 1 / sqrt(-k''(mode)) (1.5 when the curvature is unusable).
 */
function locateMode(r, psi, sigma2, a, priorConst, start) {
    let t;
    if (start !== null && Number.isFinite(start)) {
        t = Math.min(Math.max(start, LOG_W_MIN), LOG_W_MAX);
    }
    else {
        t = 0;
        let best = -Infinity;
        for (const cand of coarseNodes()) {
            const k = kernelT(cand, r, psi, sigma2, a, priorConst);
            if (k > best) {
                best = k;
                t = cand;
            }
        }
    }
    for (let it = 0; it < 25; it++) {
        const { d1, d2 } = kernelDerivs(t, r, psi, sigma2, a);
        if (!(d2 < 0) || !Number.isFinite(d1) || !Number.isFinite(d2))
            break;
        let step = -d1 / d2;
        if (step > 1.5)
            step = 1.5;
        if (step < -1.5)
            step = -1.5;
        t += step;
        if (t < LOG_W_MIN)
            t = LOG_W_MIN;
        if (t > LOG_W_MAX)
            t = LOG_W_MAX;
        if (Math.abs(step) < 1e-11)
            break;
    }
    const { d2 } = kernelDerivs(t, r, psi, sigma2, a);
    const scale = d2 < 0 && Number.isFinite(d2) ? 1 / Math.sqrt(-d2) : 1.5;
    return { mode: t, scale };
}
/** Kernel drop (nats below the modal value) that bounds the quadrature window. */
const WINDOW_DROP = 30;
/**
 * The quadrature window for one area: march outward from the mode
 * with growing steps until the kernel has dropped WINDOW_DROP nats
 * (mass beyond is below e^-40 relative), add a margin, and union
 * with the curvature window mode +/- max(6 scales, 1.2). The march
 * is what covers SKEWED posteriors — at small nu the discounted
 * outlier's posterior has a long shoulder toward the prior mode
 * that a curvature-scaled symmetric window truncates (the F-2
 * build's brute-force arbitration measured a 1e-2 log-likelihood
 * understatement from exactly that truncation).
 */
function quadratureWindow(mode, scale, r, psi, sigma2, a, priorConst) {
    const kMode = kernelT(mode, r, psi, sigma2, a, priorConst);
    const threshold = kMode - WINDOW_DROP;
    let lo = mode;
    {
        let step = Math.max(scale, 0.25);
        for (let i = 0; i < 60 && lo > LOG_W_MIN; i++) {
            const cand = Math.max(lo - step, LOG_W_MIN);
            lo = cand;
            if (kernelT(cand, r, psi, sigma2, a, priorConst) <= threshold)
                break;
            step *= 1.7;
        }
    }
    let hi = mode;
    {
        let step = Math.max(scale, 0.25);
        for (let i = 0; i < 60 && hi < LOG_W_MAX; i++) {
            const cand = Math.min(hi + step, LOG_W_MAX);
            hi = cand;
            if (kernelT(cand, r, psi, sigma2, a, priorConst) <= threshold)
                break;
            step *= 1.7;
        }
    }
    const half = Math.max(6 * scale, 1.2);
    return {
        lo: Math.max(Math.min(lo - 0.3 - 0.15 * (mode - lo), mode - half), LOG_W_MIN),
        hi: Math.min(Math.max(hi + 0.3 + 0.15 * (hi - mode), mode + half), LOG_W_MAX),
    };
}
function eStep(y, X, psi, beta, sigma2, nu, nodeCount, startModes) {
    const m = y.length;
    const a = nu / 2;
    const priorConst = a * Math.log(a) - logGamma(a);
    const B = new Array(m);
    const postVar = new Array(m);
    const ewU2 = new Array(m);
    const gammaBar = new Array(m);
    const modes = new Array(m);
    let logLik = 0;
    for (let d = 0; d < m; d++) {
        const r = y[d] - dot(X[d], beta);
        const psid = psi[d];
        const { mode, scale } = locateMode(r, psid, sigma2, a, priorConst, startModes ? startModes[d] : null);
        modes[d] = mode;
        const { lo, hi } = quadratureWindow(mode, scale, r, psid, sigma2, a, priorConst);
        // Two panels split AT the mode: Gauss-Legendre nodes cluster at
        // panel ends, so the split puts the rule's resolution on the
        // posterior spike itself. A single panel over the whole window
        // leaves the spike — narrower than the window by an order of
        // magnitude at large nu — sampled by two or three nodes and
        // understates the likelihood by ~3e-3 per area (the F-2 build's
        // trapezoid arbitration; at 128 nodes the single panel agreed
        // exactly, confirming resolution, not range, was the defect).
        const panelNodes = Math.max(4, Math.ceil(nodeCount / 2));
        const left = glRule(panelNodes, lo, mode);
        const right = glRule(panelNodes, mode, hi);
        const nodes = [...left.nodes, ...right.nodes];
        const weights = [...left.weights, ...right.weights];
        const J = nodes.length;
        const kernel = new Array(J);
        let kmax = -Infinity;
        for (let j = 0; j < J; j++) {
            const k = kernelT(nodes[j], r, psid, sigma2, a, priorConst);
            kernel[j] = k;
            if (k > kmax)
                kmax = k;
        }
        let sum = 0;
        for (let j = 0; j < J; j++) {
            kernel[j] = weights[j] * Math.exp(kernel[j] - kmax);
            sum += kernel[j];
        }
        logLik += kmax + Math.log(sum);
        let eGamma = 0;
        let eU2 = 0;
        let eWU2 = 0;
        for (let j = 0; j < J; j++) {
            const pj = kernel[j] / sum;
            const wj = Math.exp(nodes[j]);
            const s2w = sigma2 / wj;
            const gamma = s2w / (s2w + psid);
            const u2 = gamma * psid + gamma * gamma * r * r;
            eGamma += pj * gamma;
            eU2 += pj * u2;
            eWU2 += pj * wj * u2;
        }
        const Bd = r * eGamma;
        B[d] = Bd;
        postVar[d] = Math.max(0, eU2 - Bd * Bd);
        ewU2[d] = eWU2;
        gammaBar[d] = r !== 0 ? Bd / r : sigma2 / (sigma2 + psid);
    }
    return { logLik, B, postVar, ewU2, gammaBar, modes };
}
/**
 * The class's marginal log-likelihood at given parameters (exported
 * for the engine tests' independent-implementation oracle).
 */
export function tMarginalLogLik(y, X, psi, beta, sigma2, nu, nodeCount = DEFAULT_QUADRATURE_NODES) {
    if (!Number.isFinite(nu)) {
        let ll = 0;
        for (let d = 0; d < y.length; d++) {
            const r = y[d] - dot(X[d], beta);
            const varD = sigma2 + psi[d];
            ll += -0.5 * Math.log(2 * Math.PI * varD) - (r * r) / (2 * varD);
        }
        return ll;
    }
    return eStep(y, X, psi, beta, sigma2, nu, nodeCount, null).logLik;
}
function emFit(y, X, psi, nu, nodeCount, invPsiXtX, start, tolGain, cap) {
    const m = y.length;
    const p = X[0].length;
    let beta = [...start.beta];
    let sigma2 = Math.max(start.sigma2, 1e-10);
    let step = eStep(y, X, psi, beta, sigma2, nu, nodeCount, null);
    if (!Number.isFinite(step.logLik))
        return null;
    let prevL = -Infinity;
    let converged = false;
    let iterations = 0;
    for (let it = 1; it <= cap; it++) {
        iterations = it;
        const L = step.logLik;
        if (it > 1 && L - prevL < tolGain * (1 + Math.abs(L))) {
            converged = true;
            break;
        }
        prevL = L;
        // M-step: beta by psi-weighted least squares on (y - B).
        const rhs = new Array(p).fill(0);
        for (let d = 0; d < m; d++) {
            const resp = (y[d] - step.B[d]) / psi[d];
            for (let a = 0; a < p; a++)
                rhs[a] += X[d][a] * resp;
        }
        beta = matVec(invPsiXtX, rhs);
        let s2 = 0;
        for (let d = 0; d < m; d++)
            s2 += step.ewU2[d];
        sigma2 = Math.max(s2 / m, 1e-13);
        if (!beta.every(Number.isFinite) || !Number.isFinite(sigma2))
            return null;
        step = eStep(y, X, psi, beta, sigma2, nu, nodeCount, step.modes);
        if (!Number.isFinite(step.logLik))
            return null;
    }
    return { beta, sigma2, logLik: step.logLik, iterations, converged, step };
}
function normalMarginalLogLik(y, X, psi, beta, sigma2) {
    let ll = 0;
    for (let d = 0; d < y.length; d++) {
        const r = y[d] - dot(X[d], beta);
        const varD = sigma2 + psi[d];
        ll += -0.5 * Math.log(2 * Math.PI * varD) - (r * r) / (2 * varD);
    }
    return ll;
}
/** Normal marginal ML by Fisher scoring on sigma^2 with step halving on the likelihood. */
function fitNormalMl(y, X, psi, startSigma2) {
    const m = y.length;
    const p = X[0].length;
    let sigma2 = Math.max(startSigma2, 1e-10);
    const gls = (s2) => {
        const M = zeros(p, p);
        const rhs = new Array(p).fill(0);
        for (let d = 0; d < m; d++) {
            const q = 1 / (s2 + psi[d]);
            for (let a = 0; a < p; a++) {
                rhs[a] += q * X[d][a] * y[d];
                for (let b = 0; b < p; b++)
                    M[a][b] += q * X[d][a] * X[d][b];
            }
        }
        const L = cholesky(M);
        if (L === null)
            return null;
        const invM = cholInverse(L);
        return { beta: matVec(invM, rhs), invM };
    };
    let cur = gls(sigma2);
    if (cur === null)
        return null;
    let ll = normalMarginalLogLik(y, X, psi, cur.beta, sigma2);
    let converged = false;
    let iterations = 0;
    for (let it = 1; it <= 200; it++) {
        iterations = it;
        let score = 0;
        let info = 0;
        for (let d = 0; d < m; d++) {
            const r = y[d] - dot(X[d], cur.beta);
            const q = 1 / (sigma2 + psi[d]);
            score += -0.5 * q + 0.5 * q * q * r * r;
            info += 0.5 * q * q;
        }
        if (!Number.isFinite(score) || !Number.isFinite(info) || info <= 0)
            break;
        let step = score / info;
        let accepted = false;
        for (let h = 0; h < 60; h++) {
            const sNew = sigma2 + step;
            if (sNew > 0 && Number.isFinite(sNew)) {
                const cand = gls(sNew);
                if (cand !== null) {
                    const llNew = normalMarginalLogLik(y, X, psi, cand.beta, sNew);
                    if (Number.isFinite(llNew) && llNew >= ll) {
                        const gain = llNew - ll;
                        sigma2 = sNew;
                        cur = cand;
                        ll = llNew;
                        accepted = true;
                        if (gain < POLISH_TOL_GAIN * (1 + Math.abs(ll)))
                            converged = true;
                        break;
                    }
                }
            }
            step /= 2;
        }
        if (!accepted || converged)
            break;
    }
    if (!Number.isFinite(ll))
        return null;
    return { beta: cur.beta, sigma2, logLik: ll, iterations, converged };
}
function fitTCore(y, X, psi, nodeCount) {
    const m = y.length;
    const p = X[0].length;
    const sigma2Pr = prasadRaoSigma2(y, X, psi);
    const startSigma2 = Math.max(sigma2Pr, 0.1 * medianOf(psi));
    // The psi-weighted cross-product inverse (the M-step's constant).
    const XtPsiX = zeros(p, p);
    for (let d = 0; d < m; d++) {
        const wgt = 1 / psi[d];
        for (let a = 0; a < p; a++) {
            for (let b = 0; b < p; b++)
                XtPsiX[a][b] += wgt * X[d][a] * X[d][b];
        }
    }
    const LX = cholesky(XtPsiX);
    if (LX === null)
        throw singularError('psi-weighted design (X\'Psi^-1X)');
    const invPsiXtX = cholInverse(LX);
    const ml = fitNormalMl(y, X, psi, startSigma2);
    let warm = ml
        ? { beta: ml.beta, sigma2: ml.sigma2 }
        : {
            beta: (() => {
                const rhs = new Array(p).fill(0);
                for (let d = 0; d < m; d++) {
                    const resp = y[d] / psi[d];
                    for (let a = 0; a < p; a++)
                        rhs[a] += X[d][a] * resp;
                }
                return matVec(invPsiXtX, rhs);
            })(),
            sigma2: startSigma2,
        };
    const candidates = [];
    let totalIterations = ml ? ml.iterations : 0;
    // Descending scan: the first fit (nu = 25) starts from the normal
    // ML fit it nearly reduces to, and each subsequent warm start is
    // the neighbouring nu's fit — closer in (beta, sigma^2) than the
    // ascending chain from the heavy tail.
    for (const nu of [...T_NU_GRID].reverse()) {
        const em = emFit(y, X, psi, nu, nodeCount, invPsiXtX, warm, PROFILE_TOL_GAIN, PROFILE_CAP);
        if (em !== null) {
            totalIterations += em.iterations;
            candidates.push({ nu, em, ml: null, looseLogLik: em.logLik });
            warm = { beta: em.beta, sigma2: em.sigma2 };
        }
    }
    if (ml !== null) {
        candidates.push({ nu: Infinity, em: null, ml, looseLogLik: ml.logLik });
    }
    if (candidates.length === 0)
        return null;
    const bestLoose = Math.max(...candidates.map((c) => c.looseLogLik));
    // Polish every candidate within the registered window of the best
    // loose log-likelihood, then choose: highest polished likelihood,
    // ties within TIE_BAND to the larger nu (infinity largest).
    let best = null;
    let bestLogLik = -Infinity;
    for (const cand of candidates) {
        if (cand.looseLogLik < bestLoose - POLISH_WINDOW)
            continue;
        let { em } = cand;
        if (em !== null) {
            const polished = emFit(y, X, psi, cand.nu, nodeCount, invPsiXtX, { beta: em.beta, sigma2: em.sigma2 }, POLISH_TOL_GAIN, POLISH_CAP);
            if (polished !== null) {
                totalIterations += polished.iterations;
                em = polished;
            }
        }
        const ll = em !== null ? em.logLik : cand.ml.logLik;
        if (best === null ||
            ll > bestLogLik + TIE_BAND ||
            (ll >= bestLogLik - TIE_BAND && cand.nu > best.nu)) {
            best = { ...cand, em };
            bestLogLik = ll;
        }
    }
    if (best === null)
        return null;
    if (best.em !== null) {
        const em = best.em;
        if (!(em.sigma2 > SIGMA2_DECLINE_FLOOR) || !Number.isFinite(em.logLik)) {
            return null;
        }
        return {
            beta: em.beta,
            sigma2: em.sigma2,
            nu: best.nu,
            logLik: em.logLik,
            iterations: totalIterations,
            converged: em.converged,
            step: em.step,
            residuals: y.map((yd, d) => yd - dot(X[d], em.beta)),
        };
    }
    const mlFit = best.ml;
    if (!(mlFit.sigma2 > SIGMA2_DECLINE_FLOOR) || !Number.isFinite(mlFit.logLik)) {
        return null;
    }
    return {
        beta: mlFit.beta,
        sigma2: mlFit.sigma2,
        nu: Infinity,
        logLik: mlFit.logLik,
        iterations: totalIterations,
        converged: mlFit.converged,
        step: null,
        residuals: y.map((yd, d) => yd - dot(X[d], mlFit.beta)),
    };
}
/**
 * Fit one cell by the F-2 class. `fallback` is the shipped normal
 * fit, invoked unchanged on a registered decline (see the module
 * header). Validation and standardization mirror the shipped fit
 * exactly.
 */
export function fitFayHerriotTImpl(domains, config, fallback) {
    const inSample = domains.filter((d) => !d.outOfSample);
    const m = inSample.length;
    if (m < MIN_AREAS_TO_FIT)
        throw new InsufficientAreasError(m, MIN_AREAS_TO_FIT);
    const p = inSample[0].covariates.length;
    if (p < 1) {
        throw new Error('fitFayHerriot: each domain must carry at least an intercept covariate.');
    }
    if (m - p < 1) {
        throw new Error(`fitFayHerriot: residual degrees of freedom (areas - covariates = ${m - p}) ` +
            'must be at least 1 for the Prasad-Rao starting value (brief 9.7).');
    }
    for (const d of domains) {
        if (d.covariates.length !== p) {
            throw new Error(`fitFayHerriot: inconsistent covariate length for area "${d.areaId}" ` +
                `(expected ${p}, received ${d.covariates.length}).`);
        }
        for (const v of d.covariates) {
            if (!Number.isFinite(v)) {
                throw new Error(`fitFayHerriot: non-finite covariate for area "${d.areaId}".`);
            }
        }
        if (!d.outOfSample) {
            if (!Number.isFinite(d.thetaHat)) {
                throw new Error(`fitFayHerriot: non-finite thetaHat for area "${d.areaId}".`);
            }
            if (!Number.isFinite(d.psi) || d.psi <= 0) {
                throw new Error(`fitFayHerriot: psi must be positive and finite for area "${d.areaId}"; received ${d.psi}.`);
            }
        }
    }
    // Standardization (brief 9.6), identical to the shipped fit.
    const centers = new Array(p).fill(0);
    const scales = new Array(p).fill(1);
    if (config.standardizeCovariates) {
        for (let j = 1; j < p; j++) {
            const column = inSample.map((d) => d.covariates[j]);
            const sd = Math.sqrt(sampleVariance(column));
            if (sd > 0 && Number.isFinite(sd)) {
                centers[j] = meanOf(column);
                scales[j] = sd;
            }
        }
    }
    const standardizeRow = (row) => row.map((v, j) => (v - centers[j]) / scales[j]);
    const X = inSample.map((d) => standardizeRow(d.covariates));
    const y = inSample.map((d) => d.thetaHat);
    const psi = inSample.map((d) => d.psi);
    const nodeCount = config.studyQuadratureNodes ?? DEFAULT_QUADRATURE_NODES;
    const core = fitTCore(y, X, psi, nodeCount);
    if (core === null)
        return fallback(domains, config);
    const { beta, sigma2, nu } = core;
    const marginalVarU = Number.isFinite(nu) ? (sigma2 * nu) / (nu - 2) : sigma2;
    // Normal-form invM at the fitted variance (g2 and beta SEs).
    const M = zeros(p, p);
    for (let d = 0; d < m; d++) {
        const q = 1 / (sigma2 + psi[d]);
        for (let a = 0; a < p; a++) {
            for (let b = 0; b < p; b++)
                M[a][b] += q * X[d][a] * X[d][b];
        }
    }
    const LM = cholesky(M);
    if (LM === null)
        return fallback(domains, config);
    const invM = cholInverse(LM);
    const betaStandardErrors = beta.map((_, j) => Math.sqrt(Math.max(0, invM[j][j])));
    // g3 in the shipped REML form.
    let sumNeg2 = 0;
    for (let d = 0; d < m; d++)
        sumNeg2 += Math.pow(sigma2 + psi[d], -2);
    const vbar = 2 / sumNeg2;
    // Posterior quantities per in-sample area: from the E-step at the
    // fit for finite nu; the normal forms at the infinity endpoint.
    const gammaOf = (i) => core.step !== null
        ? core.step.gammaBar[i]
        : sigma2 / (sigma2 + psi[i]);
    const postVarOf = (i) => core.step !== null
        ? core.step.postVar[i]
        : (sigma2 / (sigma2 + psi[i])) * psi[i];
    const postMeanOf = (i) => core.step !== null ? core.step.B[i] : gammaOf(i) * core.residuals[i];
    let inIdx = -1;
    const areas = domains.map((d) => {
        const xs = standardizeRow(d.covariates);
        const syntheticTheta = dot(xs, beta);
        const xInvMx = dot(xs, matVec(invM, xs));
        if (d.outOfSample) {
            return {
                areaId: d.areaId,
                outOfSample: true,
                gamma: 0,
                eblupTheta: syntheticTheta,
                syntheticTheta,
                g1: 0,
                g2: 0,
                g3: 0,
                mseTheta: marginalVarU + xInvMx,
            };
        }
        inIdx += 1;
        const i = inIdx;
        const gamma = gammaOf(i);
        const g1 = postVarOf(i);
        const g2 = (1 - gamma) * (1 - gamma) * xInvMx;
        const g3 = (d.psi * d.psi * Math.pow(sigma2 + d.psi, -3)) * vbar;
        return {
            areaId: d.areaId,
            outOfSample: false,
            thetaHat: d.thetaHat,
            psi: d.psi,
            gamma,
            eblupTheta: syntheticTheta + postMeanOf(i),
            syntheticTheta,
            g1,
            g2,
            g3,
            mseTheta: g1 + g2 + 2 * g3,
        };
    });
    // Diagnostics (brief 9.16), in the shipped fit's form with the
    // class's marginal random-effect variance in the residual scale.
    const inSampleFits = areas.filter((a) => !a.outOfSample);
    const standardizedResiduals = inSample.map((d, i) => {
        const value = core.residuals[i] / Math.sqrt(marginalVarU + d.psi);
        return { areaId: d.areaId, value, flagged: Math.abs(value) > 3 };
    });
    const { skewness, kurtosis } = skewnessKurtosis(core.residuals);
    const medianGamma = medianOf(inSampleFits.map((a) => a.gamma));
    const modelMean = meanOf(inSampleFits.map((a) => a.eblupTheta));
    const directStatewide = meanOf(y);
    const relativeDifference = directStatewide !== 0
        ? (modelMean - directStatewide) / Math.abs(directStatewide)
        : modelMean === 0
            ? 0
            : Number.POSITIVE_INFINITY;
    const cvRatios = [];
    for (const a of inSampleFits) {
        if (a.thetaHat !== undefined && a.thetaHat !== 0 && a.eblupTheta !== 0) {
            const modelCv = Math.sqrt(Math.max(0, a.mseTheta)) / Math.abs(a.eblupTheta);
            const directCv = Math.sqrt(a.psi ?? 0) / Math.abs(a.thetaHat);
            if (directCv > 0)
                cvRatios.push(modelCv / directCv);
        }
    }
    const diagnostics = {
        standardizedResiduals,
        skewness,
        kurtosis,
        medianGamma,
        lowShrinkage: medianGamma < 0.1,
        aggregation: {
            modelMean,
            directStatewide,
            relativeDifference,
            warning: Math.abs(relativeDifference) > 0.05,
        },
        medianCvRatio: cvRatios.length > 0 ? medianOf(cvRatios) : 0,
    };
    // R2_FH: the class's intercept-only refit, on marginal variances.
    {
        const X0 = inSample.map(() => [1]);
        const nullCore = fitTCore(y, X0, psi, nodeCount);
        if (nullCore !== null && nullCore.sigma2 > 0) {
            const nullVarU = Number.isFinite(nullCore.nu)
                ? (nullCore.sigma2 * nullCore.nu) / (nullCore.nu - 2)
                : nullCore.sigma2;
            if (nullVarU > 0) {
                diagnostics.rSquaredFh = Math.min(1, Math.max(0, 1 - marginalVarU / nullVarU));
            }
        }
    }
    const betaOriginalScale = beta.map((bj, j) => j === 0
        ? beta[0] -
            beta.reduce((acc, bk, k) => (k === 0 ? acc : acc + (bk * centers[k]) / scales[k]), 0)
        : bj / scales[j]);
    return {
        sigma2v: sigma2,
        beta,
        betaStandardErrors,
        betaOriginalScale,
        methodUsed: 'fh-t',
        robustNu: nu,
        iterations: core.iterations,
        converged: core.converged,
        boundary: false,
        logLikMl: core.logLik,
        aic: -2 * core.logLik + 2 * (p + 2),
        bic: -2 * core.logLik + (p + 2) * Math.log(m),
        areas,
        diagnostics,
    };
}
//# sourceMappingURL=fayherriot-t.js.map